import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import type { Theme } from "../../lib/theme";
import { Logo, ThemeGlyph } from "../Logo";
import { PLANS, type PlanId } from "../../lib/pricing";
import { OnboardingWizard } from "./OnboardingWizard";
import { LiveDashboard } from "./LiveDashboard";

type View = "onboarding" | "dashboard";

export function AppShell({
  theme,
  onToggleTheme,
}: {
  theme: Theme;
  onToggleTheme: () => void;
}) {
  const { signOut } = useAuthActions();
  const tenants = useQuery(api.tenants.listMyTenants);
  const createTenant = useMutation(api.tenants.createTenant);

  const [selected, setSelected] = useState<Id<"tenants"> | null>(null);
  const [view, setView] = useState<View>("onboarding");

  const list = (tenants ?? []).filter(
    (t): t is NonNullable<typeof t> => t != null,
  );
  const loading = tenants === undefined;
  const current = list.find((t) => t._id === selected) ?? list[0] ?? null;

  async function handleCreate(name: string, plan: PlanId) {
    const id = await createTenant({ name, plan });
    setSelected(id);
    setView("onboarding");
  }

  return (
    <div className="app">
      <aside className="sidebar">
        <a href="#top" aria-label="Slackcord">
          <Logo />
        </a>

        <div className="side-section" style={{ flex: 1, minWidth: 0 }}>
          <h4>Organizations</h4>
          <div className="stack" style={{ gap: 4 }}>
            {loading && <span className="muted mono" style={{ fontSize: "0.82rem" }}>Loading…</span>}
            {!loading && list.length === 0 && (
              <span className="muted" style={{ fontSize: "0.86rem" }}>
                No organizations yet.
              </span>
            )}
            {list.map((t) => (
              <button
                key={t._id}
                className="tenant-item"
                aria-current={current?._id === t._id}
                onClick={() => setSelected(t._id)}
              >
                <span className="tenant-badge">
                  {t.name.slice(0, 1).toUpperCase()}
                </span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t.name}
                  <span className="mono" style={{ display: "block", fontSize: "0.68rem", color: "var(--ink-faint)" }}>
                    {t.plan}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="side-foot">
          <button
            className="theme-toggle"
            onClick={onToggleTheme}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          >
            <ThemeGlyph theme={theme} />
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="main">
        {current ? (
          <TenantWorkspace
            key={current._id}
            tenantId={current._id}
            name={current.name}
            plan={current.plan}
            view={view}
            onView={setView}
          />
        ) : (
          <CreateOrg onCreate={handleCreate} first={list.length === 0} loading={loading} />
        )}

        {current && (
          <div style={{ marginTop: 40 }}>
            <CreateOrgInline onCreate={handleCreate} />
          </div>
        )}
      </main>
    </div>
  );
}

function TenantWorkspace({
  tenantId,
  name,
  plan,
  view,
  onView,
}: {
  tenantId: Id<"tenants">;
  name: string;
  plan: string;
  view: View;
  onView: (v: View) => void;
}) {
  return (
    <>
      <div className="main-head">
        <div>
          <span className="eyebrow">Workspace</span>
          <h1 style={{ marginTop: 10 }}>{name}</h1>
          <span className="pill" style={{ marginTop: 10 }}>
            {plan} plan
          </span>
        </div>
        <div className="auth-tabs" role="tablist" style={{ margin: 0 }}>
          <button
            role="tab"
            aria-selected={view === "onboarding"}
            className="auth-tab"
            onClick={() => onView("onboarding")}
          >
            Onboarding
          </button>
          <button
            role="tab"
            aria-selected={view === "dashboard"}
            className="auth-tab"
            onClick={() => onView("dashboard")}
          >
            Live dashboard
          </button>
        </div>
      </div>
      {view === "onboarding" ? (
        <OnboardingWizard tenantId={tenantId} tenantName={name} />
      ) : (
        <LiveDashboard tenantName={name} />
      )}
    </>
  );
}

function CreateOrg({
  onCreate,
  first,
  loading,
}: {
  onCreate: (name: string, plan: PlanId) => Promise<void>;
  first: boolean;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }
  return (
    <div style={{ maxWidth: 520, margin: "6vh auto 0" }}>
      <span className="eyebrow">{first ? "Welcome" : "New organization"}</span>
      <h1 style={{ margin: "12px 0 8px", fontSize: "clamp(1.8rem, 3vw, 2.4rem)" }}>
        {first ? "Create your first organization" : "Create an organization"}
      </h1>
      <p className="muted" style={{ marginBottom: 24 }}>
        An organization holds one Slack → Discord migration and its team. You can
        create more later.
      </p>
      <CreateOrgForm onCreate={onCreate} />
    </div>
  );
}

function CreateOrgInline({
  onCreate,
}: {
  onCreate: (name: string, plan: PlanId) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
        + New organization
      </button>
    );
  }
  return (
    <div className="card" style={{ padding: 24, maxWidth: 520 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <b style={{ fontFamily: "var(--font-display)" }}>New organization</b>
        <button className="close-x" style={{ position: "static" }} onClick={() => setOpen(false)}>
          ×
        </button>
      </div>
      <CreateOrgForm onCreate={async (n, p) => { await onCreate(n, p); setOpen(false); }} />
    </div>
  );
}

function CreateOrgForm({
  onCreate,
}: {
  onCreate: (name: string, plan: PlanId) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [plan, setPlan] = useState<PlanId>("starter");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(name.trim(), plan);
      setName("");
    } catch (err) {
      setError("Could not create the organization. Please try again.");
      console.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <input
        className="input"
        placeholder="Organization name (e.g. Acme Inc)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label="Organization name"
        required
      />
      <div className="field">
        <label htmlFor="plan-select">
          Plan <b>{plan}</b>
        </label>
        <select
          id="plan-select"
          className="input"
          value={plan}
          onChange={(e) => setPlan(e.target.value as PlanId)}
        >
          {PLANS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.priceUsd !== null ? ` — $${p.priceUsd}` : " — custom"}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="form-error">{error}</p>}
      <button className="btn btn-primary" type="submit" disabled={busy || !name.trim()}>
        {busy ? "Creating…" : "Create organization"}
      </button>
    </form>
  );
}
